import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  SxProps,
  Theme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import "./CustomDrawer.css";
import CustomModal from "../Modal/CustomModal";
import CustomButton from "../Buttons/Button/CustomButton";

export interface CustomDrawerProps {
  // 基本的な制御
  open: boolean;
  onClose: () => void;
  anchor?: "left" | "right" | "top" | "bottom";

  // コンテンツ
  title?: string;
  children?: React.ReactNode;

  // スタイリング
  width?: string | number;
  height?: string | number;

  // ヘッダー関連
  showHeader?: boolean;
  showCloseButton?: boolean;
  headerActions?: React.ReactNode;

  // フッター関連
  showFooter?: boolean;
  footerContent?: React.ReactNode;

  // 動作設定
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  keepMounted?: boolean;

  // アクセシビリティ
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;

  // カスタムスタイル
  sx?: SxProps<Theme>;
  className?: string;

  // アニメーション
  transitionDuration?: number; // ms

  // モバイル対応
  fullScreen?: boolean;

  /**
   * 未保存ガード: true のとき、閉じ操作 (× / 背景 / Esc) で破棄確認を出し、
   * 「破棄して閉じる」を選んだ時のみ onClose を呼ぶ。
   * 通常はフォームの dirty 状態を渡す。
   */
  confirmOnDirtyClose?: boolean;
  /** 破棄確認の本文 (省略時はデフォルト文言)。 */
  dirtyCloseMessage?: string;
}

const CustomDrawer: React.FC<CustomDrawerProps> = ({
  open,
  onClose,
  anchor = "right",
  title,
  children,
  width = 400,
  height = "auto",
  showHeader = true,
  showCloseButton = true,
  headerActions,
  showFooter = false,
  footerContent,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  keepMounted: _keepMounted, // 現在は常時マウント
  sx,
  className = "",
  transitionDuration = 300,
  fullScreen = false,
  confirmOnDirtyClose = false,
  dirtyCloseMessage = "入力中の情報が失われますが、よろしいでしょうか？",
  ...aria
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 未保存ガード: 閉じ操作を一旦受けて、dirty なら破棄確認を挟む。
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const requestClose = () => {
    if (confirmOnDirtyClose) setShowDiscardConfirm(true);
    else onClose();
  };
  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };
  const cancelDiscard = () => setShowDiscardConfirm(false);

  // 初回マウント時に transform="translateX(0)" でレンダリングされると CSS transition が
  // 発火しないため、open=true への切り替えを 1 フレーム遅らせる。
  // close は即時に反映 (translateX(100%) へアニメーションさせる)。
  const [animOpen, setAnimOpen] = useState(false);
  useEffect(() => {
    if (open) {
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => setAnimOpen(true));
        (animOpenRaf as { current: number | null }).current = id2;
      });
      return () => cancelAnimationFrame(id1);
    } else {
      setAnimOpen(false);
    }
  }, [open]);
  const animOpenRaf = React.useRef<number | null>(null);
  useEffect(
    () => () => {
      if (animOpenRaf.current !== null) cancelAnimationFrame(animOpenRaf.current);
    },
    [],
  );

  // ESC キーで閉じる (未保存ガード経由)。
  // 破棄確認モーダル表示中はリスナーを張らない (Esc で確認を閉じられるようにするため。
  // 張ったままだと確認モーダルの Esc がドロワー側にも拾われ再度確認が開いてしまう)。
  useEffect(() => {
    if (!open || disableEscapeKeyDown || showDiscardConfirm) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirmOnDirtyClose) setShowDiscardConfirm(true);
      else onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, disableEscapeKeyDown, onClose, confirmOnDirtyClose, showDiscardConfirm]);

  // 背景のスクロールを無効にする
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
    };
  }, [open]);

  const drawerWidth =
    isMobile && fullScreen
      ? "100%"
      : anchor === "top" || anchor === "bottom"
        ? "100%"
        : width;

  const drawerHeight =
    anchor === "top" || anchor === "bottom"
      ? height
      : isMobile && fullScreen
        ? "100vh"
        : "100vh";

  const getDrawerStyles = () => {
    const baseStyles = {
      position: "fixed" as const,
      backgroundColor: "#fff",
      // 影は開いている時だけ出す。CustomDrawer は閉じても常時マウントされ画面外へ
      // translate退避するだけなので、影 (ぼかし24px) を常時付けると退避先の端が
      // 画面端に滲んで「閉じているのに影だけ残る」状態になる。animOpen で連動させて防ぐ。
      boxShadow: animOpen ? "0px 1px 24px 0px #5b5b5b40" : "none",
      transition: `transform ${transitionDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
      zIndex: 1300,
      display: "flex",
      flexDirection: "column" as const,
      ...(isMobile &&
        fullScreen && {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          maxWidth: "100vw",
          maxHeight: "100vh",
        }),
    };

    let styles: any;
    switch (anchor) {
      case "right":
        styles = {
          ...baseStyles,
          top: 0,
          right: 0,
          width: drawerWidth,
          height: drawerHeight,
          transform: animOpen ? "translateX(0)" : "translateX(100%)",
        };
        break;
      case "left":
        styles = {
          ...baseStyles,
          top: 0,
          left: 0,
          width: drawerWidth,
          height: drawerHeight,
          transform: animOpen ? "translateX(0)" : "translateX(-100%)",
        };
        break;
      case "top":
        styles = {
          ...baseStyles,
          top: 0,
          left: 0,
          width: drawerWidth,
          height: drawerHeight,
          transform: animOpen ? "translateY(0)" : "translateY(-100%)",
        };
        break;
      case "bottom":
        styles = {
          ...baseStyles,
          bottom: 0,
          left: 0,
          width: drawerWidth,
          height: drawerHeight,
          transform: animOpen ? "translateY(0)" : "translateY(100%)",
        };
        break;
      default:
        styles = baseStyles;
    }

    return styles;
  };

  return (
    <>
      {/* Backdrop */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          opacity: animOpen ? 1 : 0,
          visibility: open ? "visible" : "hidden",
          // 開く時は visibility を即時 visible に、閉じる時は opacity フェード後に hidden 化するため delay を入れる
          transition: `opacity ${transitionDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), visibility 0s linear ${open ? "0s" : `${transitionDuration}ms`}`,
          zIndex: 1200,
          pointerEvents: animOpen ? "auto" : "none",
        }}
        onClick={() => !disableBackdropClick && requestClose()}
      />

      {/* Drawer */}
      <Box
        sx={[getDrawerStyles(), sx]}
        className={`customDrawer ${isMobile ? "sp" : ""} ${className}`}
        data-anchor={anchor}
        {...aria}
      >
        <Box className="drawerContainer">
          {showHeader && (
            <Box className="drawerHeader">
              <Box className="drawerHeaderContent">
                {title && (
                  <Typography className="drawerTitle">{title}</Typography>
                )}
                <Box className="drawerHeaderActions">
                  {headerActions}
                  {showCloseButton && (
                    <IconButton
                      onClick={requestClose}
                      className="drawerCloseButton"
                      size="small"
                    >
                      <Close />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Box className="drawerBody">{children}</Box>

          {showFooter && footerContent && (
            <Box className="drawerFooter">{footerContent}</Box>
          )}
        </Box>
      </Box>

      {/* 未保存ガード: dirty 状態で閉じようとした時の破棄確認 (× / 背景 / Esc 共通)。
          既存の離脱確認 (CustomFormNavigationGuard) と同じデザインを踏襲する。 */}
      {confirmOnDirtyClose && (
        <CustomModal
          openModal={showDiscardConfirm}
          handleCloseModal={cancelDiscard}
          title="ページ移動の確認"
          bodyText={dirtyCloseMessage}
          modalType="deleteModal"
          bodyTextAlign="left"
          hideCloseButton
          sx={{ padding: "0px !important" }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={cancelDiscard}
              sx={{ width: "50% !important" }}
            />
            <CustomButton
              label="移動"
              buttonCategory="confirm"
              onClick={confirmDiscard}
              sx={{ width: "50% !important" }}
            />
          </Box>
        </CustomModal>
      )}
    </>
  );
};

export default CustomDrawer;
